import { useContext, useEffect } from "react";
import { UNSAFE_NavigationContext, useSearchParams } from "react-router-dom";
import { FalhaDaApi } from "@/api/problema";

/*
 * Os filtros de uma lista, na URL: Boletos, Cedentes, Convênios, Entregas e Auditoria.
 *
 * Filtro na URL é o que torna um estado de tela COMUNICÁVEL: o link no chat abre a
 * mesma lista, e F5 não perde nada. Digitação usa `replace`: quem digita oito letras
 * não quer apertar voltar oito vezes.
 *
 * Só as chaves declaradas são tocadas: `aba` e o que mais estiver na consulta
 * sobrevivem a "Limpar filtros".
 */
export interface DefinicaoDeFiltro<C extends string> {
  chave: C;
  /** O rótulo do chip. */
  rotulo: string;
  /** O valor no chip, quando o da URL não se lê: `756` vira `756 Sicoob`. */
  textoDoValor?: (valor: string) => string;
  /**
   * Para filtro de enum: valor recusado é filtro não aplicado, sem chip e sem ir à consulta.
   * A URL é digitável, e o que a API não conhece viraria `400` numa lista que abre bem sem ele.
   */
  aceita?: (valor: string) => boolean;
}

export interface FiltroAplicado<C extends string> {
  chave: C;
  rotulo: string;
  texto: string;
}

export interface FiltrosNaUrl<C extends string> {
  /** Para o corpo da consulta: `undefined` é filtro não aplicado. */
  valores: Record<C, string | undefined>;
  /** O cursor da página, que entra na consulta junto dos filtros. */
  cursor: string | undefined;
  /** O que vira chip, na ordem das definições. */
  aplicados: readonly FiltroAplicado<C>[];
  /** Vazio remove o filtro. */
  ajustar: (chave: C, valor: string) => void;
  limpar: () => void;
}

/*
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ A CONSULTA NOVA SAI DA URL DE AGORA, E NÃO DA URL DO ÚLTIMO RENDER.      │
 * │                                                                          │
 * │ O roteador escreve no histórico na hora e pinta depois, numa transição.  │
 * │ Dois ajustes antes da pintura partiriam da mesma consulta, e o segundo   │
 * │ apagaria o primeiro: era o valor máximo levando o mínimo em Boletos. O   │
 * │ `setSearchParams` com função não resolve, porque recebe os parâmetros do │
 * │ mesmo render.                                                            │
 * │                                                                          │
 * │ Quem sabe a URL de agora é o histórico do roteador, e `location` não     │
 * │ está no tipo `Navigator`. `window.location` só valeria com               │
 * │ `BrowserRouter`: sob `MemoryRouter`, limparia os filtros que não vê.     │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
function useConsultaDeAgora(): () => URLSearchParams {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const historico = navigator as unknown as { location: Pick<Location, "search"> };

  return () => new URLSearchParams(historico.location.search);
}

export function useFiltrosNaUrl<C extends string>(
  definicoes: readonly DefinicaoDeFiltro<C>[],
): FiltrosNaUrl<C> {
  const [parametros, setParametros] = useSearchParams();
  const consultaDeAgora = useConsultaDeAgora();

  const valores = Object.fromEntries(
    definicoes.map(({ chave, aceita }) => {
      const valor = parametros.get(chave) || undefined;
      return [chave, valor !== undefined && aceita?.(valor) === false ? undefined : valor];
    }),
  ) as Record<C, string | undefined>;

  const aplicados = definicoes.flatMap(({ chave, rotulo, textoDoValor }) => {
    const valor = valores[chave];
    if (valor === undefined) return [];
    return [{ chave, rotulo, texto: textoDoValor === undefined ? valor : textoDoValor(valor) }];
  });

  /*
   * Mudar o filtro SEMPRE tira o cursor: a posição era da lista anterior, e a busca
   * nova abriria no meio do resultado, sem nada dizendo isso.
   */
  function mudar(aplicar: (proximos: URLSearchParams) => void) {
    const proximos = consultaDeAgora();
    aplicar(proximos);
    proximos.delete("cursor");
    setParametros(proximos, { replace: true });
  }

  return {
    valores,
    cursor: parametros.get("cursor") ?? undefined,
    aplicados,
    ajustar: (chave, valor) =>
      mudar((proximos) => {
        if (valor === "") proximos.delete(chave);
        else proximos.set(chave, valor);
      }),
    limpar: () =>
      mudar((proximos) => {
        for (const { chave } of definicoes) proximos.delete(chave);
      }),
  };
}

/**
 * O erro que a lista mostra, sem o `cursor_invalido`.
 *
 * Esse código não é defeito para ler: o item do cursor foi apagado, ou o link veio de
 * outra lista, e a saída é uma só. O cursor sai da URL, com `replace`, e a lista volta
 * à primeira página. A entrada ruim não fica no histórico, e o voltar não cai de novo
 * nela. `recuperando` segura o esqueleto nesse meio tempo, para a tela não piscar
 * "Nenhum cedente." antes da primeira página chegar.
 */
export function useErroDaPagina<E>(erro: E | null): { erro: E | null; recuperando: boolean } {
  const [, setParametros] = useSearchParams();
  const recuperando = erro instanceof FalhaDaApi && erro.codigo === "cursor_invalido";

  useEffect(() => {
    if (!recuperando) return;
    setParametros(
      (atuais) => {
        const proximos = new URLSearchParams(atuais);
        proximos.delete("cursor");
        return proximos;
      },
      { replace: true },
    );
  }, [recuperando, setParametros]);

  return { erro: recuperando ? null : erro, recuperando };
}
